import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Residential General Contracting",
  "Custom Home",
  "Renovation",
  "Addition",
  "Kitchen or Bath",
  "ADU",
  "Not sure yet",
];

const PHONE = "715-470-0677";
const PHONE_HREF = "tel:+17154700677";
const EMAIL = "zach@sativapanel.com";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: PROJECT_TYPES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thanks — we got your request. We'll be in touch within 1 business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: PROJECT_TYPES[0],
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Couldn't send your message. Please try again or call us directly.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F4F3EF] py-24 sm:py-32 border-t border-[#DCD9CE]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left — info */}
        <div className="reveal lg:col-span-5">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#9E8262]">
            05 — Start a Project
          </div>
          <h2 className="font-display font-bold text-[#1A1D1A] mt-5 text-4xl sm:text-5xl lg:text-[56px] leading-[1.04] tracking-tight">
            Let&apos;s build
            <br />
            <span className="italic font-light text-[#2C4230]">something good.</span>
          </h2>
          <p className="font-body text-[#4A524A] mt-7 text-base sm:text-lg leading-relaxed max-w-md">
            Tell us a bit about your project. We&apos;ll set up a no-pressure
            conversation — usually within 24 hours.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={PHONE_HREF}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 border border-[#DCD9CE] flex items-center justify-center text-[#2C4230] group-hover:bg-[#2C4230] group-hover:text-[#F4F3EF] transition-colors duration-300">
                <Phone size={18} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#9E8262] font-semibold">
                  Call
                </div>
                <div className="font-display font-bold text-[#1A1D1A] text-lg mt-0.5 group-hover:text-[#2C4230] transition-colors">
                  {PHONE}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 border border-[#DCD9CE] flex items-center justify-center text-[#2C4230] group-hover:bg-[#2C4230] group-hover:text-[#F4F3EF] transition-colors duration-300">
                <Mail size={18} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#9E8262] font-semibold">
                  Email
                </div>
                <div className="font-display font-bold text-[#1A1D1A] text-lg mt-0.5 group-hover:text-[#2C4230] transition-colors break-all">
                  {EMAIL}
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4" data-testid="contact-location">
              <div className="w-12 h-12 border border-[#DCD9CE] flex items-center justify-center text-[#2C4230]">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#9E8262] font-semibold">
                  Service Area
                </div>
                <div className="font-display font-bold text-[#1A1D1A] text-lg mt-0.5">
                  Central Wisconsin
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="reveal lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-[#EBE9E2] border border-[#DCD9CE] p-8 sm:p-12"
          >
            <h3 className="font-display font-bold text-[#1A1D1A] text-2xl sm:text-3xl tracking-tight">
              Request a Quote
            </h3>
            <p className="font-body text-[#4A524A] text-sm mt-2">
              All fields except phone are required.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <Field
                label="Your Name"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                testId="contact-input-name"
                autoComplete="name"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                testId="contact-input-email"
                autoComplete="email"
              />
              <Field
                label="Phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={onChange}
                testId="contact-input-phone"
                autoComplete="tel"
              />
              <SelectField
                label="Project Type"
                name="project_type"
                value={form.project_type}
                onChange={onChange}
                options={PROJECT_TYPES}
                testId="contact-input-project-type"
              />
            </div>

            <div className="mt-8">
              <label
                htmlFor="message"
                className="text-[11px] tracking-[0.2em] uppercase text-[#4A524A] font-semibold block mb-3"
              >
                Tell us about your project
              </label>
              <textarea
                id="message"
                name="message"
                data-testid="contact-input-message"
                rows={5}
                value={form.message}
                onChange={onChange}
                required
                placeholder="Square footage, timing, must-haves, anything weird about the lot…"
                className="w-full bg-transparent border-b border-[#2C4230]/30 py-3 focus:outline-none focus:border-[#2C4230] transition-colors resize-none text-[#1A1D1A] placeholder:text-[#4A524A]/50 font-body"
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-[#2C4230] text-[#F4F3EF] hover:bg-[#213124] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 px-8 py-4 text-[13px] font-semibold tracking-wide"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : submitted ? (
                  <>
                    <Check size={16} />
                    Sent — Send Another
                  </>
                ) : (
                  <>Send Request</>
                )}
              </button>
              <p className="text-xs text-[#4A524A]/80 font-body">
                We respond within one business day.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testId, autoComplete }) => (
  <div>
    <label
      htmlFor={name}
      className="text-[11px] tracking-[0.2em] uppercase text-[#4A524A] font-semibold block mb-3"
    >
      {label}
      {required && <span className="text-[#9E8262]"> *</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      autoComplete={autoComplete}
      data-testid={testId}
      className="w-full bg-transparent border-b border-[#2C4230]/30 py-3 focus:outline-none focus:border-[#2C4230] transition-colors text-[#1A1D1A] placeholder:text-[#4A524A]/50 font-body"
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, testId }) => (
  <div>
    <label
      htmlFor={name}
      className="text-[11px] tracking-[0.2em] uppercase text-[#4A524A] font-semibold block mb-3"
    >
      {label}
    </label>
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-transparent border-b border-[#2C4230]/30 py-3 focus:outline-none focus:border-[#2C4230] transition-colors text-[#1A1D1A] font-body appearance-none cursor-pointer"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232C4230' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 0.25rem center",
        backgroundSize: "12px",
      }}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);
